/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-restricted-syntax */
import {
  ApolloClient,
  from,
  NormalizedCacheObject,
  Observable,
} from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { ErrorResponse, onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { SchemaLink } from '@apollo/client/link/schema';
import { ServerError } from '@apollo/client/link/utils';
import { addMocksToSchema } from '@graphql-tools/mock';
import config from 'config';
import schema from 'apollo/schema';
import cache, { useMocksCache } from 'apollo/cache';
import mocks from 'apollo/mocks';
import { CustomError, CodeMessage, ExtendedErrorContext } from 'apollo/types';
import ErrorHandler from 'error/ErrorHandler';
import utils from 'utils/apiService/utils';

const { apiUrl, useMocks } = config;
const { refreshTokensRequest, logout } = utils;

interface RefreshTokenResponse {
  data: {
    IdToken: string;
    AccessToken: string;
  };
}

const onErrorRefreshRequest = ({ operation, forward }: ErrorResponse) =>
  new Observable((observer) => {
    refreshTokensRequest()
      .then((response: RefreshTokenResponse) => {
        const purchaseIntentId = localStorage.getItem('purchaseIntentId');
        localStorage.setItem('IdToken', response.data.IdToken);
        localStorage.setItem('AccessToken', response.data.AccessToken);
        operation.setContext(({ headers = {} }) => ({
          headers: {
            ...headers,
            authorization: `Bearer ${response.data.IdToken}` || null,
            // eslint-disable-next-line prettier/prettier
            customer_purchase_intent_id: purchaseIntentId || '',
          },
        }));
      })
      .then(() => {
        const subscriber = {
          next: observer.next.bind(observer),
          error: observer.error.bind(observer),
          complete: observer.complete.bind(observer),
        };
        forward(operation).subscribe(subscriber);
      })
      .catch((error) => {
        logout();
        observer.error(error);
      });
  });

const httpUploadLink = createUploadLink({
  uri: apiUrl,
});

const errorLink = onError(
  ({ operation, forward, graphQLErrors, networkError }: ErrorResponse): any => {
    // 1. Manejar errores GraphQL
    if (graphQLErrors) {
      for (const gqlError of graphQLErrors) {
        const extensions = gqlError.extensions || {};

        // Autenticación - Intentar renovar token
        if (
          typeof extensions.name === 'string' &&
          (extensions.name.includes('NotAuthorizedException') ||
           extensions.type === 'UNAUTHORIZED' ||
           extensions.status === 401)
        ) {
          return onErrorRefreshRequest({ operation, forward });
        }
        console.log('GraphQL Error:', gqlError);
        console.log('GraphQL Error:', networkError);
        console.log('GraphQL Error:', operation);

        // Extraer información detallada del error
        const wrappedError: CustomError = {
          // Información principal del error
          type: extensions.type ? `${extensions.type as string}` : 'FATAL',
          name: extensions.name ? `${extensions.name as string}` : 'UNKNOWN',
          message: gqlError.message,

          // Información específica para errores de negocio
          codeMessage: extensions.codeMessage as CodeMessage,

          // Información de contexto adicional
          status: extensions.status as number,
          requestId: extensions.requestId as string,
          context: extensions as ExtendedErrorContext,

          // Información para errores de validación de campos
          fieldErrors: extensions.fieldErrors as Record<string, string[]>,

          // Metadata de la operación para debugging
          operationName: operation.operationName,
          path: gqlError.path?.join('.'),
        };

        // Log detallado en consola para depuración (solo en desarrollo)
        if (process.env.NODE_ENV === 'development') {
          console.group(`📡 Error GraphQL: ${wrappedError.name}`);
          console.log('Tipo:', wrappedError.type);
          console.log('Mensaje:', wrappedError.message);
          console.log('Operación:', operation.operationName);
          console.log('Variables:', operation.variables);
          console.log('Path:', gqlError.path);
          console.log('Extensiones:', extensions);
          console.groupEnd();
        }

        // Enviar al manejador de errores para UI
        ErrorHandler(wrappedError);

        // Si es un error crítico que impide la operación, detener
        if (
          wrappedError.type === 'FATAL' ||
          wrappedError.type === 'UNAUTHORIZED' ||
          wrappedError.type === 'FORBIDDEN'
        ) {
          return Observable.of(operation);
        }
      }

      // Si hay errores pero no son críticos, continuar con la operación
      return forward(operation);
    }

    // 2. Manejar errores de red
    if (networkError) {
      // Auth error de red - intentar renovar token
      if (
        'statusCode' in networkError &&
        (networkError as ServerError).statusCode === 401
      ) {
        return onErrorRefreshRequest({ operation, forward });
      }

      // Clasificar errores de red por tipo
      let errorType = 'FATAL';
      let errorName = 'NETWORK_ERROR';
      let errorMsg = networkError.message;

      // Categorizar errores de red comunes
      if ('statusCode' in networkError) {
        const { statusCode } = networkError as ServerError;

        if (statusCode >= 500) {
          errorType = 'SERVICE';
          errorName = 'SERVER_ERROR';
          errorMsg = `Error del servidor (${statusCode}): ${errorMsg}`;
        } else if (statusCode === 404) {
          errorType = 'NOT_FOUND';
          errorName = 'RESOURCE_NOT_FOUND';
        } else if (statusCode === 403) {
          errorType = 'FORBIDDEN';
          errorName = 'ACCESS_DENIED';
        } else if (statusCode === 429) {
          errorType = 'SERVICE';
          errorName = 'RATE_LIMITED';
          errorMsg = 'Demasiadas solicitudes. Por favor intente más tarde.';
        }
      } else if (networkError.message.includes('Failed to fetch')) {
        errorType = 'SERVICE';
        errorName = 'CONNECTION_ERROR';
        errorMsg = 'Error de conexión. Verifique su conexión a internet.';
      }

      // Construir error para UI
      const wrappedError: CustomError = {
        type: errorType,
        name: errorName,
        message: errorMsg,
        operationName: operation.operationName,
        status: 'statusCode' in networkError ?
          (networkError as ServerError).statusCode : undefined,
      };

      // Log para depuración
      if (process.env.NODE_ENV === 'development') {
        console.error('Error de red:', {
          status: 'statusCode' in networkError ?
            (networkError as ServerError).statusCode : 'unknown',
          message: networkError.message,
          operation: operation.operationName,
          variables: operation.variables,
          error: networkError
        });
      }

      // Enviar al manejador de errores
      ErrorHandler(wrappedError);

      // Para errores de red, generalmente detenemos la operación
      return Observable.of(operation);
    }

    // Si no hay errores, continuar normalmente
    return forward(operation);
  },
);

const authLink = setContext((operation, { headers }) => {
  const token = localStorage.getItem('IdToken');
  const purchaseIntentId = localStorage.getItem('purchaseIntentId');

  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      customer_purchase_intent_id: purchaseIntentId || '',
    },
  };
});

const schemaWithMocks = addMocksToSchema({
  schema,
  mocks,
});

const schemaLink = new SchemaLink({ schema: schemaWithMocks });

const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpUploadLink]),
  cache,
});

export const mockClient = new ApolloClient({
  ssrMode: typeof window === 'undefined',
  cache,
  link: from([errorLink, schemaLink]),
});

const selectApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  if (useMocks) {
    useMocksCache(true);
    return mockClient;
  }
  return apolloClient;
};

export default selectApolloClient;
